import type { StorageAdapter } from "./adapters";
import { uid } from "./util";

export interface QueuedAction {
  id: string;
  // Queue endpoint segment under /v1/* (e.g. "messages").
  type: "messages";
  payload: Record<string, unknown>;
  createdAt: number;
}

const QUEUE_KEY = "wherd:queue";

// Durable FIFO queue so feedback submitted offline is retried later.
export class OfflineQueue {
  private items: QueuedAction[] = [];
  private loaded = false;

  constructor(private readonly storage: StorageAdapter) {}

  async load(): Promise<void> {
    if (this.loaded) return;
    const raw = await this.storage.getItem(QUEUE_KEY);
    if (raw) {
      try {
        this.items = JSON.parse(raw) as QueuedAction[];
      } catch {
        this.items = [];
      }
    }
    this.loaded = true;
  }

  private async persist(): Promise<void> {
    await this.storage.setItem(QUEUE_KEY, JSON.stringify(this.items));
  }

  async enqueue(type: QueuedAction["type"], payload: Record<string, unknown>): Promise<QueuedAction> {
    await this.load();
    const action: QueuedAction = { id: uid(), type, payload, createdAt: Date.now() };
    this.items.push(action);
    await this.persist();
    return action;
  }

  async remove(id: string): Promise<void> {
    await this.load();
    this.items = this.items.filter((a) => a.id !== id);
    await this.persist();
  }

  async list(): Promise<QueuedAction[]> {
    await this.load();
    return [...this.items];
  }
}
